// GA4 (gtag) + Consent Mode v2 — тот же счётчик, что и в приложении квестов
// (play.algarve-quest.com), чтобы путь посетителя был одним: сайт → приложение → оплата.
// До согласия GA работает без cookies. Выбор хранится в cookie на домене
// algarve-quest.com и действует на обоих поддоменах — баннер не спрашивает дважды.
// Только для браузера: вызывается из main.tsx, при пререндере не исполняется.

const MEASUREMENT_ID = 'G-1D931G5TH9';
const CONSENT_KEY = 'aq_analytics_consent';
const COOKIE_DOMAIN = 'algarve-quest.com';
const PLAY_HOST = 'play.algarve-quest.com';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const UTM_STORAGE_KEY = 'aq_utm';

type Consent = 'granted' | 'denied';

type GtagWindow = Window & { dataLayer?: unknown[] };

function gtag(..._args: unknown[]): void {
  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer || [];
  // gtag.js читает именно объект arguments, а не массив.
  // eslint-disable-next-line prefer-rest-params
  w.dataLayer.push(arguments);
}

let initialized = false;

export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });
  if (getStoredConsent() === 'granted') applyConsent(true);

  gtag('js', new Date());
  // page_view отправляем сами при каждой смене страницы (см. Layout).
  gtag('config', MEASUREMENT_ID, { send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

function readConsentCookie(): Consent | null {
  const match = document.cookie.match(/(?:^|; )aq_consent=(granted|denied)/);
  return match ? (match[1] as Consent) : null;
}

export function getStoredConsent(): Consent | null {
  try {
    return (localStorage.getItem(CONSENT_KEY) as Consent | null) || readConsentCookie();
  } catch {
    return readConsentCookie();
  }
}

function applyConsent(granted: boolean): void {
  const value = granted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
}

export function setConsent(granted: boolean): void {
  const value: Consent = granted ? 'granted' : 'denied';
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // приватный режим — выбор сохранится только в cookie
  }
  if (window.location.hostname.endsWith(COOKIE_DOMAIN)) {
    document.cookie = `aq_consent=${value}; domain=.${COOKIE_DOMAIN}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax; Secure`;
  }
  applyConsent(granted);
}

export function trackPageView(): void {
  if (typeof window === 'undefined') return;
  gtag('event', 'page_view', {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  });
}

export function trackEvent(name: string, params: Record<string, string>): void {
  if (typeof window === 'undefined') return;
  gtag('event', name, params);
}

// --- UTM: метки из ссылки (рассылка, Instagram, QR) живут до закрытия вкладки
// и дописываются к переходам в приложение, чтобы оплата знала источник.

export function captureUtm(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 100);
    }
    if (Object.keys(utm).length > 0) sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // хранилище недоступно — атрибуция best-effort
  }
}

function getUtm(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(UTM_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function decorate(anchor: HTMLAnchorElement): void {
  if (anchor.hostname !== PLAY_HOST) return;
  const utm = getUtm();
  if (Object.keys(utm).length === 0) return;
  const url = new URL(anchor.href);
  for (const [key, value] of Object.entries(utm)) {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  anchor.href = url.toString();
}

/** Дописывает метки к ссылке на приложение в момент клика (в HTML ссылки остаются чистыми для SEO). */
export function installLinkDecorator(): void {
  const handler = (e: Event) => {
    const anchor = (e.target as Element | null)?.closest?.('a[href]');
    if (anchor instanceof HTMLAnchorElement) decorate(anchor);
  };
  // pointerdown/contextmenu — чтобы метки были и при «открыть в новой вкладке».
  for (const type of ['pointerdown', 'click', 'auxclick', 'contextmenu', 'keydown']) {
    document.addEventListener(type, handler, { capture: true });
  }
}
